import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ImageLightboxProps {
  isOpen: boolean;
  imageSrc: string;
  imageAlt: string;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

const ImageLightbox = ({
  isOpen,
  imageSrc,
  imageAlt,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false
}: ImageLightboxProps) => {
  // Prevent body scroll and handle keyboard navigation
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) onPrevious();
        if (e.key === 'ArrowRight' && hasNext && onNext) onNext();
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose, onPrevious, onNext, hasPrevious, hasNext]);

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/95 z-50 cursor-zoom-out"
          />

          {/* Modal Content */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Close Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, delay: 0.1 }}
              onClick={onClose}
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Fechar lightbox"
            >
              <FaTimes size={24} />
            </motion.button>

            {/* Previous Button - Smaller on Mobile */}
            {hasPrevious && onPrevious && (
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                onClick={onPrevious}
                className="absolute left-2 md:left-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-2 md:p-4 rounded-full transition-all duration-300 hover:scale-110"
                aria-label="Imagem anterior"
              >
                <FaChevronLeft className="text-base md:text-2xl" />
              </motion.button>
            )}

            {/* Next Button - Smaller on Mobile */}
            {hasNext && onNext && (
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                onClick={onNext}
                className="absolute right-2 md:right-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-2 md:p-4 rounded-full transition-all duration-300 hover:scale-110"
                aria-label="Próxima imagem"
              >
                <FaChevronRight className="text-base md:text-2xl" />
              </motion.button>
            )}

            {/* Image */}
            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              src={imageSrc}
              alt={imageAlt}
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default ImageLightbox;
